/**
 * ListDetailView component for viewing/editing a notification list
 * Design based on NotificationAuditLog - header, stats, search, table
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, Mail, Phone, Filter, UserMinus, 
  X, AlertCircle, Search, Check
} from 'lucide-react';
import {
  NotificationList,
  NotificationChannel,
  FilterCriteria,
  DateFilterType,
  ListCreateRequest,
  PreviewRecipient,
  getList,
  updateList,
  previewList,
} from '../../utils/notificationsBroadcastApi';

interface ListDetailViewProps {
  listId: string;
  onBack: () => void;
  isNew?: boolean;
  startEditing?: boolean;
}

const SPECIES_OPTIONS = [
  { value: 'dog', label: 'Dogs' },
  { value: 'cat', label: 'Cats' },
  { value: 'bird', label: 'Birds' },
  { value: 'reptile', label: 'Reptiles' },
  { value: 'small mammal', label: 'Small Mammals' },
  { value: 'exotic', label: 'Exotic' },
];

const STATUS_OPTIONS = [
  { value: 'PROSPECT', label: 'Prospect' },
  { value: 'OPEN', label: 'Active' },
  { value: 'COLD', label: 'Needs Appt' },
  { value: 'LOST', label: 'Lost' },
];

export const ListDetailView: React.FC<ListDetailViewProps> = ({ listId, onBack, isNew, startEditing }) => {
  const navigate = useNavigate();
  const [list, setList] = useState<NotificationList | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [recipients, setRecipients] = useState<PreviewRecipient[]>([]);
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [eligibleCount, setEligibleCount] = useState(0);
  const [optedOutCount, setOptedOutCount] = useState(0);

  // Edit state is derived from URL (RESTful) - not internal state
  const isEditing = isNew || startEditing || false;
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formListType, setFormListType] = useState<'email' | 'phone'>('email');
  const [formSpecies, setFormSpecies] = useState<string[]>([]);
  const [formCustomerStatus, setFormCustomerStatus] = useState<string[]>([]);
  const [formDateFilterType, setFormDateFilterType] = useState<DateFilterType | ''>('');
  const [formDateAfter, setFormDateAfter] = useState('');
  const [formDateBefore, setFormDateBefore] = useState('');
  const [formSubmitting, setFormSubmitting] = useState(false);

  const loadList = async () => {
    try {
      setLoading(true);
      const data = await getList(listId);
      setList(data);
      // Populate form with existing data
      setFormName(data.name);
      setFormDescription(data.description || '');
      setFormListType(data.list_type);
      setFormSpecies(data.filter_criteria.species || []);
      setFormCustomerStatus(data.filter_criteria.customer_status || []);
      setFormDateFilterType(data.filter_criteria.customer_created_date_type || '');
      setFormDateAfter(data.filter_criteria.customer_created_after || '');
      setFormDateBefore(data.filter_criteria.customer_created_before || '');
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load list');
    } finally {
      setLoading(false);
    }
  };

  const loadRecipients = async () => {
    if (!list) return;
    try {
      setRecipientsLoading(true);
      const channel: NotificationChannel = list.list_type === 'email' ? 'email_html' : 'sms';
      const preview = await previewList(listId, channel);
      setRecipients(preview.sample_recipients);
      setTotalCount(preview.total_matching);
      setEligibleCount(preview.eligible_count);
      setOptedOutCount(preview.opted_out_count);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load recipients');
    } finally {
      setRecipientsLoading(false);
    }
  };

  useEffect(() => {
    loadList();
  }, [listId]);

  useEffect(() => {
    if (list) {
      loadRecipients();
    }
  }, [list]);

  // Handler to exit edit mode via URL navigation (RESTful)
  const exitEditMode = useCallback(() => {
    navigate(`/dashboard/audience?listId=${listId}`);
  }, [navigate, listId]);

  // Handle escape key
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      if (isEditing && !isNew) {
        exitEditMode();
      } else {
        onBack();
      }
    }
  }, [isEditing, isNew, onBack, exitEditMode]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormSubmitting(true);

    const filterCriteria: FilterCriteria = {
      species: formSpecies.length > 0 ? formSpecies : undefined,
      customer_status: formCustomerStatus.length > 0 ? formCustomerStatus : undefined,
    };

    // Add date filter if specified
    if (formDateFilterType) {
      filterCriteria.customer_created_date_type = formDateFilterType;
      if (formDateFilterType === 'after' && formDateAfter) {
        filterCriteria.customer_created_after = formDateAfter;
      } else if (formDateFilterType === 'before' && formDateBefore) {
        filterCriteria.customer_created_before = formDateBefore;
      } else if (formDateFilterType === 'between' && formDateAfter && formDateBefore) {
        filterCriteria.customer_created_after = formDateAfter;
        filterCriteria.customer_created_before = formDateBefore;
      }
    }

    const data: ListCreateRequest = {
      name: formName,
      description: formDescription || undefined,
      list_type: formListType,
      filter_criteria: filterCriteria,
    };

    try {
      const updated = await updateList(listId, data);
      setList(updated);
      // Exit edit mode via navigation (RESTful)
      navigate(`/dashboard/audience?listId=${listId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save list');
    } finally {
      setFormSubmitting(false);
    }
  };

  const toggleSpecies = (species: string) => {
    setFormSpecies((prev) =>
      prev.includes(species) ? prev.filter((s) => s !== species) : [...prev, species]
    );
  };

  const toggleStatus = (status: string) => {
    setFormCustomerStatus((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
    );
  };

  const filteredRecipients = recipients.filter(r =>
    r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (r.email && r.email.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (r.phone && r.phone.includes(searchQuery))
  );

  const getFilterSummary = () => {
    const parts: string[] = [];
    if (formSpecies.length > 0) {
      parts.push(formSpecies.join(', '));
    }
    if (formCustomerStatus.length > 0) {
      parts.push(formCustomerStatus.join(', '));
    }
    return parts.length > 0 ? parts.join(' • ') : 'All customers';
  };

  // Helper to format date nicely
  const formatDateNice = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Generate human-readable description for the orange box
  const getHumanReadableDescription = useMemo(() => {
    if (!list) return '';
    
    const species = list.filter_criteria.species || [];
    const statuses = list.filter_criteria.customer_status || [];
    const dateType = list.filter_criteria.customer_created_date_type;
    const dateAfter = list.filter_criteria.customer_created_after;
    const dateBefore = list.filter_criteria.customer_created_before;
    const contactType = list.list_type === 'email' ? 'email' : 'SMS';
    
    // Build status part (lowercase)
    let statusText = '';
    if (statuses.length > 0) {
      const statusLabels = statuses.map(s => {
        if (s === 'PROSPECT') return 'prospect';
        if (s === 'OPEN') return 'active';
        if (s === 'COLD') return 'needs appointment';
        if (s === 'LOST') return 'lost';
        return s.toLowerCase();
      });
      statusText = statusLabels.join(' and ');
    } else {
      statusText = 'all';
    }
    
    // Build species part (lowercase)
    let speciesText = '';
    if (species.length > 0) {
      // Keep species lowercase
      const speciesLabels = species.map(s => s.toLowerCase());
      if (speciesLabels.length === 1) {
        speciesText = `${speciesLabels[0]} owners`;
      } else if (speciesLabels.length === 2) {
        speciesText = `${speciesLabels[0]} and ${speciesLabels[1]} owners`;
      } else {
        const last = speciesLabels.pop();
        speciesText = `${speciesLabels.join(', ')}, and ${last} owners`;
      }
    } else {
      speciesText = 'owners';
    }

    // Build date part
    let dateText = '';
    if (dateType && (dateAfter || dateBefore)) {
      if (dateType === 'after' && dateAfter) {
        dateText = ` created after ${formatDateNice(dateAfter)}`;
      } else if (dateType === 'before' && dateBefore) {
        dateText = ` created before ${formatDateNice(dateBefore)}`;
      } else if (dateType === 'between' && dateAfter && dateBefore) {
        dateText = ` created between ${formatDateNice(dateAfter)} and ${formatDateNice(dateBefore)}`;
      }
    }
    
    // Capitalize first letter of the sentence
    const sentence = `${statusText} ${speciesText}${dateText} who opted into ${contactType} messages`;
    return sentence.charAt(0).toUpperCase() + sentence.slice(1);
  }, [list]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!list) {
    return (
      <div className="p-6">
        <div className="text-center text-gray-500 dark:text-dark-text-muted">
          List not found
        </div>
      </div>
    );
  }

  // If editing, show the edit form as a full page
  if (isEditing) {
    return (
      <div className="h-full flex flex-col bg-gray-50 dark:bg-dark-bg-primary">
        {error && (
          <div className="mx-6 mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {error}
            <button onClick={() => setError(null)} className="ml-auto">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Edit Form */}
        <div className="flex-1 overflow-auto p-6">
          <div className="max-w-2xl mx-auto bg-white dark:bg-dark-bg-secondary rounded-lg border border-gray-200 dark:border-dark-border p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                  Audience Name
                </label>
                <input
                  type="text"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-lg dark:bg-dark-bg-tertiary dark:text-dark-text-primary text-sm focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Contact Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                  Contact Type
                </label>
                <div className="flex gap-3">
                  <label
                    className={`flex items-center gap-2 px-4 py-2.5 border rounded-lg cursor-pointer transition-colors ${
                      formListType === 'email'
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                        : 'border-gray-300 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary text-gray-700 dark:text-dark-text-primary'
                    }`}
                  >
                    <input
                      type="radio"
                      checked={formListType === 'email'}
                      onChange={() => setFormListType('email')}
                      className="sr-only"
                    />
                    <Mail className="w-4 h-4" />
                    <span className="text-sm font-medium">Email</span>
                  </label>
                  <label
                    className={`flex items-center gap-2 px-4 py-2.5 border rounded-lg cursor-pointer transition-colors ${
                      formListType === 'phone'
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                        : 'border-gray-300 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary text-gray-700 dark:text-dark-text-primary'
                    }`}
                  >
                    <input
                      type="radio"
                      checked={formListType === 'phone'}
                      onChange={() => setFormListType('phone')}
                      className="sr-only"
                    />
                    <Phone className="w-4 h-4" />
                    <span className="text-sm font-medium">SMS</span>
                  </label>
                </div>
              </div>

              {/* Filters */}
              <div className="border border-gray-200 dark:border-dark-border rounded-lg p-4 bg-gray-50 dark:bg-dark-bg-tertiary">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-4">
                  <Filter className="w-4 h-4" />
                  Filters
                </div>

                <div className="space-y-4">
                  {/* Species Filter */}
                  <div>
                    <label className="block text-sm text-gray-600 dark:text-dark-text-muted mb-2">
                      Pet Species
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {SPECIES_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => toggleSpecies(option.value)}
                          className={`px-3 py-1.5 text-sm font-medium border rounded-lg transition-colors ${
                            formSpecies.includes(option.value)
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                              : 'border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg-secondary text-gray-700 dark:text-dark-text-primary hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Customer Status Filter */}
                  <div>
                    <label className="block text-sm text-gray-600 dark:text-dark-text-muted mb-2">
                      Customer Status
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {STATUS_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => toggleStatus(option.value)}
                          className={`px-3 py-1.5 text-sm font-medium border rounded-lg transition-colors ${
                            formCustomerStatus.includes(option.value)
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                              : 'border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg-secondary text-gray-700 dark:text-dark-text-primary hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Customer Created Date Filter */}
                  <div>
                    <label className="block text-sm text-gray-600 dark:text-dark-text-muted mb-2">
                      Customer Created Date <span className="text-xs">(optional)</span>
                    </label>
                    <div className="space-y-3">
                      <div className="flex flex-wrap gap-2">
                        {[
                          { value: '', label: 'Any Time' },
                          { value: 'after', label: 'After' },
                          { value: 'before', label: 'Before' },
                          { value: 'between', label: 'Between' },
                        ].map((option) => (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() => {
                              setFormDateFilterType(option.value as DateFilterType | '');
                              // Clear dates when changing filter type
                              if (option.value === '') {
                                setFormDateAfter('');
                                setFormDateBefore('');
                              }
                            }}
                            className={`px-3 py-1.5 text-sm font-medium border rounded-lg transition-colors ${
                              formDateFilterType === option.value
                                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                                : 'border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg-secondary text-gray-700 dark:text-dark-text-primary hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary'
                            }`}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                      
                      {/* Date Inputs */}
                      {formDateFilterType && (
                        <div className="flex flex-wrap items-center gap-2">
                          {(formDateFilterType === 'after' || formDateFilterType === 'between') && (
                            <div className="flex items-center gap-2">
                              {formDateFilterType === 'between' && (
                                <span className="text-sm text-gray-500 dark:text-dark-text-muted">From</span>
                              )}
                              <input
                                type="date"
                                value={formDateAfter}
                                onChange={(e) => setFormDateAfter(e.target.value)}
                                className="px-3 py-1.5 text-sm border border-gray-300 dark:border-dark-border rounded-lg dark:bg-dark-bg-secondary dark:text-dark-text-primary focus:ring-blue-500 focus:border-blue-500"
                              />
                            </div>
                          )}
                          {(formDateFilterType === 'before' || formDateFilterType === 'between') && (
                            <div className="flex items-center gap-2">
                              {formDateFilterType === 'between' && (
                                <span className="text-sm text-gray-500 dark:text-dark-text-muted">To</span>
                              )}
                              <input
                                type="date"
                                value={formDateBefore}
                                onChange={(e) => setFormDateBefore(e.target.value)}
                                className="px-3 py-1.5 text-sm border border-gray-300 dark:border-dark-border rounded-lg dark:bg-dark-bg-secondary dark:text-dark-text-primary focus:ring-blue-500 focus:border-blue-500"
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-dark-border">
                <button
                  type="button"
                  onClick={exitEditMode}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-dark-text-primary bg-white dark:bg-dark-bg-secondary border border-gray-300 dark:border-dark-border rounded-lg hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={formSubmitting || !formName}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {formSubmitting ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {error && (
        <div className="mx-6 mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Header - Stats and Search */}
      <div className="p-6 border-b border-gray-200 dark:border-dark-border bg-white dark:bg-dark-bg-secondary">
        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="rounded-lg p-4 bg-gray-50 dark:bg-dark-bg-tertiary">
            <div className="text-2xl font-bold text-gray-900 dark:text-dark-text-primary">
              {totalCount}
            </div>
            <div className="text-sm text-gray-500 dark:text-dark-text-muted">Total Matching</div>
          </div>
          <div className="rounded-lg p-4 bg-green-50 dark:bg-green-900/20">
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
              {eligibleCount}
            </div>
            <div className="text-sm text-gray-500 dark:text-dark-text-muted">Eligible</div>
          </div>
          <div className="rounded-lg p-4 bg-amber-50 dark:bg-amber-900/20">
            <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">
              {optedOutCount}
            </div>
            <div className="text-sm text-gray-500 dark:text-dark-text-muted">Opted Out</div>
          </div>
        </div>

        {/* Audience Description Box */}
        <div className="rounded-lg p-4 mb-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
          <p className="text-blue-800 dark:text-blue-300 font-medium">
            {getHumanReadableDescription}
          </p>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, email, or phone..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-dark-border rounded-lg bg-white dark:bg-dark-bg-tertiary text-gray-900 dark:text-dark-text-primary placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Recipients Table */}
      <div className="flex-1 overflow-auto">
        {recipientsLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : filteredRecipients.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500 dark:text-dark-text-muted">
            <Users className="w-12 h-12 mb-3 opacity-50" />
            <p className="text-lg font-medium">No recipients found</p>
            <p className="text-sm">
              {searchQuery ? 'Try adjusting your search' : 'Adjust your filters to include customers'}
            </p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-dark-bg-tertiary sticky top-0">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                  Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                  {list.list_type === 'email' ? 'Email' : 'Phone'}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-dark-bg-secondary divide-y divide-gray-200 dark:divide-dark-border">
              {filteredRecipients.map((recipient) => (
                <tr
                  key={recipient.id}
                  className="hover:bg-gray-50 dark:hover:bg-dark-bg-hover transition-colors"
                >
                  <td className="px-4 py-3 whitespace-nowrap">
                    {recipient.notifications_enabled ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                        <Check className="w-3 h-3" />
                        Subscribed
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                        <UserMinus className="w-3 h-3" />
                        Opted Out
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-sm font-medium">
                        {recipient.name.charAt(0).toUpperCase()}
                      </div>
                      <span className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">
                        {recipient.name}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="text-sm text-gray-600 dark:text-dark-text-secondary">
                      {list.list_type === 'email' ? recipient.email : recipient.phone}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ListDetailView;
